import { Badge, FocusRing } from '@change/ds-core-ui';
import { ErrorMessage, Input, TextField } from '@change/ds-forms';
import { HeartFilledIcon } from '@change/ds-icons/HeartFilledIcon';
import { cn } from '@change/ds-utils';

import { useAmountSelector } from './hooks/useAmountSelector';

const BUTTON_BASE_CLASS = cn(
	'relative flex-1 cursor-pointer rounded-md border px-2 py-4',
	'text-size-body text-center transition-colors',
	'text-typography-information',
);

const BUTTON_DEFAULT_CLASS = cn(BUTTON_BASE_CLASS, 'border-stroke-info-weak');
const BUTTON_SELECTED_CLASS = cn(
	BUTTON_BASE_CLASS,
	'outline-stroke-info-strong bg-fill-information-weaker border-0 outline-2',
);

// The symbol span renders in-flow inside the DS field box and sizes to its content (the previous
// w-4! clipped three-character symbols like CHF) — only its colour is ours to set.
const CURRENCY_SYMBOL_CLASS = '[&>div>div>span]:text-typography-information';

export const SimpleAmountSelector: React.FC = () => {
	const {
		data: {
			buttonAmounts,
			selection,
			customAmount,
			customAmountLabel,
			currencySymbol,
			currencySymbolPosition,
			amountErrorText,
		},
		actions: { handleAmountChange, handleCustomAmountFocus, handleCustomAmountChange },
	} = useAmountSelector();

	const isGrid = buttonAmounts.length > 3;

	return (
		<div>
			<div
				role="radiogroup"
				aria-label=""
				className={isGrid ? 'grid grid-cols-3 gap-2' : 'flex flex-row gap-2'}
				data-qa="promotion-impression-amount-selector"
			>
				{buttonAmounts.map(({ value, valueDisplay, suggestedLabel }) => (
					<FocusRing key={value} focusRingClass="rounded-md">
						<button
							data-qa="suggested-amount-button"
							type="button"
							// oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- not fixed to avoid regression risk from an automated markup rewrite; needs manual accessibility review
							role="radio"
							aria-checked={selection === value}
							onClick={() => handleAmountChange(value)}
							className={selection === value ? BUTTON_SELECTED_CLASS : BUTTON_DEFAULT_CLASS}
						>
							{suggestedLabel && (
								<div className="absolute -top-3 left-1/2 -translate-x-1/2 whitespace-nowrap [&>div>span>svg]:h-2.5">
									<Badge
										label={suggestedLabel}
										leadingIcon={<HeartFilledIcon size={8} />}
										size="tiny"
										variant="information"
									/>
								</div>
							)}
							{valueDisplay}
						</button>
					</FocusRing>
				))}
				<FocusRing focusRingClass="rounded-md">
					<button
						type="button"
						// oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- not fixed to avoid regression risk from an automated markup rewrite; needs manual accessibility review
						role="radio"
						aria-checked={selection === 'custom'}
						onClick={() => handleAmountChange('custom')}
						className={cn(
							selection === 'custom' ? BUTTON_SELECTED_CLASS : BUTTON_DEFAULT_CLASS,
							isGrid && 'col-span-3',
						)}
					>
						{customAmountLabel}
					</button>
				</FocusRing>
			</div>

			{selection === 'custom' && (
				<>
					<div className={CURRENCY_SYMBOL_CLASS}>
						<TextField
							label={customAmountLabel}
							labelIsVisible={false}
							name="customAmount"
							value={customAmount}
							inputMode="numeric"
							data-qa="custom-amount-input"
							isInvalid={!!amountErrorText}
							input={
								<Input
									onFocus={handleCustomAmountFocus}
									// className styles the DS field box; [&_input]: reaches the input element for the
									// value colour. The old pl-6 indent is now the slot's job.
									className="border-stroke-focus [&_input]:text-typography-information mt-4 h-12 border-2 focus-within:border-transparent"
									onChange={handleCustomAmountChange}
									leadingIcon={currencySymbolPosition === 'before' ? <span>{currencySymbol}</span> : undefined}
									trailingIcon={currencySymbolPosition === 'after' ? <span>{currencySymbol}</span> : undefined}
								/>
							}
						/>
					</div>
					{amountErrorText && (
						<div data-qa="custom-amount-input-error-message">
							<ErrorMessage size="small">{amountErrorText}</ErrorMessage>
						</div>
					)}
				</>
			)}
		</div>
	);
};
